import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronDown, Anchor, Check, MapPin, Phone, Mail, Calculator, Box, Globe, Search, Facebook, Linkedin, MessageCircle } from 'lucide-react';
import { Navbar } from './components/Navbar';
// import { Assistant } from './components/Assistant'; // AI Assistant disabled for Cloudflare deployment
import { ServiceDetail } from './components/ServiceDetail';
import { LocationDetail } from './components/LocationDetail';
import { QuoteRequest } from './components/QuoteRequest';
import { TrackingSearch } from './components/TrackingSearch';
import { TrackingResult } from './components/TrackingResult';
import { ThankYou } from './components/ThankYou';
import { CONTENT } from './constants';
import { Language, Service, ShipmentData } from './types';

function App() {
  const [lang, setLang] = useState<Language>('en');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [showLocations, setShowLocations] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showTrackingSearch, setShowTrackingSearch] = useState(false);
  const [trackingData, setTrackingData] = useState<ShipmentData | null>(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [showThankYou, setShowThankYou] = useState(false);

  // Check URL for /thanks path
  useEffect(() => {
    if (window.location.pathname === '/thanks') {
      setShowThankYou(true);
    }
  }, []);
  
  const t = CONTENT[lang];
  const isRTL = lang === 'ar';

  const partnerLogos = [
    { name: "Partner 1", image: "/comp1-removebg-preview.png", height: "h-16" },
    { name: "Partner 2", image: "/comp2-removebg-preview.png", height: "h-16" },
    { name: "Partner 3", image: "/comp3-removebg-preview.png", height: "h-16" },
    { name: "Partner 4", image: "/comp4-removebg-preview.png", height: "h-16" },
    { name: "Partner 5", image: "/comp5-removebg-preview.png", height: "h-16" },
    { name: "Partner 6", image: "/comp6-removebg-preview.png", height: "h-16" },
    { name: "Partner 7", image: "/comp7-removebg-preview.png", height: "h-16" },
    { name: "Partner 8", image: "/comp8-removebg-preview.png", height: "h-16" },
    { name: "Partner 9", image: "/comp9-removebg-preview.png", height: "h-16" },
    { name: "Partner 10", image: "/comp10-removebg-preview.png", height: "h-16" },
  ];

  const handleTrack = (id: string) => {
    // Simulate finding a shipment
    const mockData: ShipmentData = {
      id: id,
      origin: "Shanghai, CN",
      destination: "Benghazi, LY",
      status: "In Transit",
      estimatedDelivery: "Oct 24, 2025",
      events: [
        { date: "Oct 12", time: "08:30 AM", location: "Malta Freeport", description: "Transshipment arrival", status: "current" },
        { date: "Oct 05", time: "14:20 PM", location: "Shanghai Port", description: "Vessel departed", status: "completed" },
        { date: "Oct 03", time: "09:15 AM", location: "Shanghai Warehouse", description: "Cargo received", status: "completed" },
      ]
    };
    setTrackingData(mockData);
    setShowTrackingSearch(false);
  };

  return (
    <div className={`min-h-screen bg-luxury-paper text-luxury-charcoal ${lang === 'ar' ? 'font-arabic' : 'font-sans'} selection:bg-greenline-900 selection:text-white`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar lang={lang} setLang={setLang} />

      {/* HERO SECTION - CORPORATE EDITORIAL */}
      <section className="relative h-screen w-full overflow-hidden bg-luxury-black flex flex-col justify-between">
        <div className="absolute inset-0 z-0">
           {/* Cinematic Video/Image Background */}
           <img 
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2670&auto=format&fit=crop"
            className="w-full h-full object-cover opacity-50 grayscale hover:scale-105 transition-transform duration-[20s] ease-in-out"
            alt="Global Logistics Network"
           />
           <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/30"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 flex-grow flex items-center px-6 md:px-12 max-w-[1920px] mx-auto w-full pt-20">
          <div className="max-w-4xl animate-fade-up">
            <span className="inline-block border border-greenline-400/50 text-greenline-400 px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] mb-8 backdrop-blur-md">
               Global Logistics Solutions
            </span>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9] tracking-tight mb-8">
              {lang === 'ar' ? 'شريكك اللوجستي' : 'Your Trusted'} <br />
              <span className="italic font-light text-white/90">{lang === 'ar' ? 'الموثوق' : 'Logistics Partner.'}</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/70 font-light max-w-xl leading-relaxed mb-10">
              {t.hero.subtitle}
            </p>
          </div>
        </div>

        {/* THE UTILITY BAR - Styled Luxuriously */}
        <div className="relative z-20 bg-white/5 backdrop-blur-xl border-t border-white/10 w-full animate-fade-in">
           <div className="max-w-[1920px] mx-auto grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
              
              {/* Action 1 - Get Quote */}
              <button 
                onClick={() => setShowQuote(true)}
                className="group relative h-32 md:h-40 px-8 flex flex-col justify-center items-start text-left hover:bg-white/5 transition-colors w-full"
              >
                  <div className="flex items-center gap-3 mb-3 text-greenline-400 group-hover:text-white transition-colors">
                     <Calculator size={20} strokeWidth={1.5} />
                     <span className="text-[10px] uppercase tracking-[0.3em] font-bold">{lang === 'ar' ? 'طلب عرض سعر' : 'Get A Quote'}</span>
                  </div>
                  <span className="font-serif text-2xl md:text-3xl text-white italic group-hover:translate-x-2 transition-transform duration-500 rtl:group-hover:-translate-x-2">
                     {lang === 'ar' ? 'حساب تكلفة الشحن' : 'Rate & Transit Times'}
                  </span>
                  <ArrowRight className="absolute right-8 bottom-8 text-white/30 group-hover:text-white transition-colors rtl:left-8 rtl:right-auto rtl:rotate-180" size={24} strokeWidth={1} />
              </button>

              {/* Action 2 - Tracking */}
              <button 
                onClick={() => setShowTrackingSearch(true)}
                className="group relative h-32 md:h-40 px-8 flex flex-col justify-center items-start text-left hover:bg-white/5 transition-colors w-full"
              >
                  <div className="flex items-center gap-3 mb-3 text-greenline-400 group-hover:text-white transition-colors">
                     <Box size={20} strokeWidth={1.5} />
                     <span className="text-[10px] uppercase tracking-[0.3em] font-bold">{lang === 'ar' ? 'تتبع شحنتك' : 'Track Shipment'}</span>
                  </div>
                  <span className="font-serif text-2xl md:text-3xl text-white italic group-hover:translate-x-2 transition-transform duration-500 rtl:group-hover:-translate-x-2">
                     {lang === 'ar' ? 'الحالة في الوقت الفعلي' : 'Real-time Status'}
                  </span>
                  <ArrowRight className="absolute right-8 bottom-8 text-white/30 group-hover:text-white transition-colors rtl:left-8 rtl:right-auto rtl:rotate-180" size={24} strokeWidth={1} />
              </button>

              {/* Action 3 - Locations */}
              <button 
                onClick={() => setShowLocations(true)}
                className="group relative h-32 md:h-40 px-8 flex flex-col justify-center items-start text-left hover:bg-white/5 transition-colors w-full"
              >
                  <div className="flex items-center gap-3 mb-3 text-greenline-400 group-hover:text-white transition-colors">
                     <Globe size={20} strokeWidth={1.5} />
                     <span className="text-[10px] uppercase tracking-[0.3em] font-bold">{lang === 'ar' ? 'شبكتنا' : 'Locations'}</span>
                  </div>
                  <span className="font-serif text-2xl md:text-3xl text-white italic group-hover:translate-x-2 transition-transform duration-500 rtl:group-hover:-translate-x-2">
                     {lang === 'ar' ? 'ابحث عن فرع' : 'Find GreenLine Offices'}
                  </span>
                  <ArrowRight className="absolute right-8 bottom-8 text-white/30 group-hover:text-white transition-colors rtl:left-8 rtl:right-auto rtl:rotate-180" size={24} strokeWidth={1} />
              </button>

           </div>
        </div>
      </section>

      {/* ABOUT - Editorial Layout */}
      <section id="about" className="py-24 md:py-40 px-6 md:px-12 max-w-[1920px] mx-auto bg-white scroll-mt-32">
         <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
            
            {/* Image Block */}
            <div className="lg:col-span-5 relative">
               <div className="aspect-[3/4] overflow-hidden bg-stone-100">
                  <img
                     src="/benghazi_Port.jpg"
                     alt="Benghazi Port Operations"
                     className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000 ease-in-out"
                  />
               </div>
               <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-luxury-black p-8 flex flex-col justify-center items-center text-center hidden md:flex animate-fade-up">
                  <span className="text-greenline-400 font-serif text-4xl italic mb-1">100%</span>
                  <span className="text-white text-[10px] uppercase tracking-[0.2em] leading-tight">Secure Handling</span>
               </div>
            </div>

            {/* Content Block */}
            <div className="lg:col-span-7">
               <div className="flex items-center gap-4 mb-12">
                  <div className="h-px w-12 bg-luxury-black"></div>
                  <span className="text-xs uppercase tracking-[0.3em] font-medium">{t.about.title}</span>
               </div>
               
               <h2 className="font-serif text-5xl md:text-7xl mb-12 leading-[1.1] text-luxury-black">
                  {t.about.subtitle}
               </h2>
               
               <p className="text-xl font-light leading-relaxed text-stone-600 mb-16 max-w-2xl text-balance">
                  {t.about.description}
               </p>

               <div className="grid grid-cols-2 gap-12 border-t border-stone-200 pt-12">
                  <div>
                     <span className="font-serif text-6xl block mb-2 text-greenline-900">{t.about.stats.experience}</span>
                     <span className="text-[10px] uppercase tracking-[0.3em] text-stone-500">{t.about.stats.experienceLabel}</span>
                  </div>
                  <div>
                     <span className="font-serif text-6xl block mb-2 text-greenline-900">{t.about.stats.clients}</span>
                     <span className="text-[10px] uppercase tracking-[0.3em] text-stone-500">{t.about.stats.clientsLabel}</span>
                  </div>
               </div>
            </div>
         </div>
      </section>

      {/* SERVICES - CORPORATE GRID (Bento Box Style) */}
      <section id="services" className="py-24 md:py-40 bg-luxury-paper border-t border-stone-200 scroll-mt-32">
         <div className="px-6 md:px-12 max-w-[1920px] mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
               <div className="max-w-2xl">
                  <h2 className="font-serif text-5xl md:text-7xl mb-6 text-luxury-black">{t.services.title}</h2>
                  <p className="text-stone-500 text-lg md:text-xl font-light">{t.services.subtitle}</p>
               </div>
               {/* Button removed */}
            </div>

            {/* The Service Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
               {t.services.items.map((service, index) => {
                  const Icon = service.icon;
                  return (
                     <button 
                        key={index} 
                        onClick={() => setSelectedService(service)}
                        className="group relative bg-white h-96 p-10 flex flex-col justify-between hover:bg-stone-50 transition-colors text-left w-full"
                     >
                        <div className="flex justify-between items-start w-full">
                           <div className="w-12 h-12 rounded-full border border-stone-200 flex items-center justify-center text-luxury-black group-hover:bg-luxury-black group-hover:text-white group-hover:border-luxury-black transition-all duration-300">
                              <Icon size={20} strokeWidth={1} />
                           </div>
                           <span className="text-stone-300 font-serif text-2xl italic">0{index + 1}</span>
                        </div>
                        
                        <div>
                           <h3 className="font-serif text-3xl mb-4 text-luxury-black group-hover:translate-x-2 transition-transform duration-300 rtl:group-hover:-translate-x-2">{service.title}</h3>
                           <p className="text-stone-500 text-sm leading-relaxed mb-6 border-l border-stone-200 pl-4 line-clamp-2 rtl:border-l-0 rtl:border-r rtl:pl-0 rtl:pr-4">
                              {service.description}
                           </p>
                           <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-greenline-900 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                              {lang === 'ar' ? 'اقرأ المزيد' : 'Learn More'} <ArrowRight size={12} className="rtl:rotate-180" />
                           </span>
                        </div>
                     </button>
                  );
               })}
            </div>
         </div>
      </section>

      {/* CLIENTS & PARTNERS - Rolling Bar */}
      <section className="py-24 bg-white border-t border-stone-200 overflow-hidden">
        <div className="px-6 md:px-12 max-w-[1920px] mx-auto mb-16 text-center">
            <span className="text-[10px] uppercase tracking-[0.3em] text-greenline-900 block mb-4">{t.clients.subtitle}</span>
            <h2 className="font-serif text-4xl md:text-5xl text-luxury-black mb-6">{t.clients.title}</h2>
            <p className="max-w-2xl mx-auto text-stone-500 font-light">{t.clients.description}</p>
        </div>
        
        <div className="flex overflow-hidden py-10 bg-stone-50">
           <div className="flex animate-marquee whitespace-nowrap">
              {[...partnerLogos, ...partnerLogos, ...partnerLogos].map((logo, index) => (
                <div key={index} className="mx-16 flex items-center justify-center min-w-[200px]">
                  <img
                    src={logo.image}
                    alt={logo.name}
                    className={`${logo.height} w-auto object-contain grayscale brightness-0 contrast-100 opacity-30 hover:opacity-100 hover:brightness-100 hover:grayscale-0 transition-all duration-500`}
                    style={{ filter: 'grayscale(100%) brightness(0.6) contrast(1.2) drop-shadow(0 0 0 transparent)' }}
                    onMouseEnter={(e) => e.currentTarget.style.filter = 'none'}
                    onMouseLeave={(e) => e.currentTarget.style.filter = 'grayscale(100%) brightness(0.6) contrast(1.2) drop-shadow(0 0 0 transparent)'}
                    onError={(e) => {
                      e.currentTarget.src = '';
                      e.currentTarget.parentElement?.classList.add('hidden');
                    }}
                  />
                </div>
              ))}
           </div>
        </div>
      </section>

      {/* PROCESS - Corporate Timeline */}
      <section className="py-24 bg-luxury-black text-white">
         <div className="px-6 md:px-12 max-w-[1920px] mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-24 border-b border-white/10 pb-8">
               <h2 className="font-serif text-4xl md:text-5xl italic text-white/90">{t.process.title}</h2>
               <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-4 md:mt-0">Workflow Methodology</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
               {t.process.steps.map((step, idx) => (
                  <div key={idx} className="bg-luxury-black p-10 group hover:bg-white/5 transition-colors duration-500">
                     <span className="text-4xl font-serif text-white/20 group-hover:text-greenline-400 transition-colors mb-8 block">0{idx + 1}</span>
                     <h3 className="text-lg uppercase tracking-widest mb-4 font-bold">{step.title}</h3>
                     <p className="text-white/50 text-sm font-light leading-relaxed">{step.desc}</p>
                  </div>
               ))}
            </div>
         </div>
      </section>

      {/* MEET THE TEAM - Professional Grid */}
      <section className="py-24 bg-stone-50 border-t border-stone-200">
         <div className="px-6 md:px-12 max-w-[1920px] mx-auto">
            <div className="text-center mb-20 max-w-2xl mx-auto">
              <span className="text-[10px] uppercase tracking-[0.3em] text-greenline-900 block mb-4">{t.team.title}</span>
              <h2 className="font-serif text-4xl md:text-5xl text-luxury-black">{t.team.subtitle}</h2>
            </div>
            
            <div className="flex flex-wrap justify-center gap-8 md:gap-12">
              {t.team.members.map((member, idx) => (
                <div key={idx} className="group w-full md:w-[calc(50%-2rem)] lg:w-[calc(33.33%-2rem)] max-w-sm bg-white shadow-sm hover:shadow-xl transition-all duration-500">
                  <div className="aspect-[4/5] overflow-hidden relative">
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-in-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-8 text-center relative">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-greenline-900 text-white px-4 py-1 text-[10px] uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-all duration-300 delay-100">
                      Green Line Team
                    </div>
                    <h3 className="font-serif text-xl font-medium mb-2">{member.name}</h3>
                    <p className="text-xs uppercase tracking-widest text-greenline-700 font-bold mb-6">{member.role}</p>
                    
                    <div className="space-y-3 pt-4 border-t border-stone-100">
                       {member.email && (
                          <a href={`mailto:${member.email}`} className="flex items-center justify-center gap-2 text-xs text-stone-500 hover:text-greenline-900 transition-colors">
                             <Mail size={12} /> {member.email}
                          </a>
                       )}
                       <a href={`tel:${member.phone}`} className="flex items-center justify-center gap-2 text-xs text-stone-500 hover:text-greenline-900 transition-colors">
                          <Phone size={12} /> {member.phone}
                       </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
         </div>
      </section>

      {/* FOOTER - Professional & Clean */}
      <footer id="contact" className="bg-white pt-24 pb-8 px-6 md:px-12 border-t border-stone-200 scroll-mt-32">
         <div className="max-w-[1920px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 mb-24">
               
               {/* Contact Info */}
               <div>
                  <img 
                    src="/Picture1-1920w.webp" 
                    alt="GreenLine Logistics" 
                    className="h-16 w-auto object-contain mb-12 origin-left"
                  />
                  <div className="space-y-12">
                     <div className="max-w-md">
                        <span className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-4">
                           <MapPin size={14} /> Headquarters
                        </span>
                        <p className="font-serif text-2xl leading-relaxed text-luxury-black">
                           {t.contact.address}
                        </p>
                     </div>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                         <div>
                            <span className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-4">
                               <Phone size={14} /> Phone
                            </span>
                            <a href={`tel:${t.contact.phone}`} className="block font-serif text-xl hover:text-greenline-900 transition-colors" dir="ltr">{t.contact.phone}</a>
                         </div>
                         <div>
                            <span className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-4">
                               <Mail size={14} /> Email
                            </span>
                            <a href={`mailto:${t.contact.email}`} className="block font-serif text-xl hover:text-greenline-900 transition-colors">{t.contact.email}</a>
                         </div>
                     </div>

                      {/* Social / Messaging Links */}
                      <div>
                        <span className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-4">
                          Connect
                        </span>
                        <div className="flex items-center gap-4">
                          <a
                            href="https://www.facebook.com/profile.php?id=61567528345954#"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook"
                            className="p-2 border border-stone-200 hover:border-greenline-900 hover:text-greenline-900 transition-colors text-stone-600"
                            title="Facebook"
                          >
                            <Facebook size={18} />
                          </a>
                          <a
                            href="https://wa.me/218928376938"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="WhatsApp"
                            className="p-2 border border-stone-200 hover:border-greenline-900 hover:text-greenline-900 transition-colors text-stone-600"
                            title="WhatsApp"
                          >
                            <MessageCircle size={18} />
                          </a>
                          <a
                            href="https://ly.linkedin.com/in/greenline-office-68a72436b"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="LinkedIn"
                            className="p-2 border border-stone-200 hover:border-greenline-900 hover:text-greenline-900 transition-colors text-stone-600"
                            title="LinkedIn"
                          >
                            <Linkedin size={18} />
                          </a>
                        </div>
                      </div>
                  </div>
               </div>

               {/* Inquiry Form */}
               <div className="bg-stone-50 p-8 md:p-12">
                  <span className="block text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-8">{t.contact.subtitle}</span>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      try {
                        const formDataToSend = new FormData();
                        formDataToSend.append('kind', 'contact');
                        formDataToSend.append('name', contactForm.name);
                        formDataToSend.append('email', contactForm.email);
                        formDataToSend.append('phone', contactForm.phone);
                        formDataToSend.append('message', contactForm.message);
                        
                        const response = await fetch('/api/enquiry', {
                          method: 'POST',
                          body: formDataToSend,
                        });

                        if (response.ok) {
                          window.location.href = '/thanks';
                        } else {
                          alert(lang === 'ar' ? 'حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.' : 'Error submitting form. Please try again.');
                        }
                      } catch (error) {
                        console.error('Submit error:', error);
                        alert(lang === 'ar' ? 'حدث خطأ أثناء الإرسال. يرجى المحاولة مرة أخرى.' : 'Error submitting form. Please try again.');
                      }
                    }}
                    className="space-y-6"
                  >
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <input
                          type="text"
                          placeholder={t.contact.form.name}
                          className="w-full bg-white border-none p-4 text-sm placeholder-stone-400 focus:ring-1 focus:ring-greenline-900 transition-all"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          required
                        />
                        <input
                          type="email"
                          placeholder={t.contact.form.email}
                          className="w-full bg-white border-none p-4 text-sm placeholder-stone-400 focus:ring-1 focus:ring-greenline-900 transition-all"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          required
                        />
                     </div>
                     <input
                       type="tel"
                       placeholder={t.contact.form.phone}
                       className="w-full bg-white border-none p-4 text-sm placeholder-stone-400 focus:ring-1 focus:ring-greenline-900 transition-all"
                       value={contactForm.phone}
                       onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                     />
                     <textarea
                       rows={4}
                       placeholder={t.contact.form.message}
                       className="w-full bg-white border-none p-4 text-sm placeholder-stone-400 focus:ring-1 focus:ring-greenline-900 transition-all resize-none"
                       value={contactForm.message}
                       onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                       required
                     ></textarea>
                     <button className="bg-luxury-black text-white w-full py-4 text-xs uppercase tracking-[0.3em] hover:bg-greenline-900 transition-colors">
                        {t.contact.form.submit}
                     </button>
                  </form>
               </div>
            </div>

            {/* Bottom Bar */}
            <div className="flex flex-col md:flex-row justify-between items-center border-t border-stone-200 pt-8 gap-4">
               <span className="text-[10px] uppercase tracking-widest text-stone-400">{t.footer.legal}</span>
               <div className="flex gap-8">
                  <a href="#" className="text-[10px] uppercase tracking-widest text-stone-400 hover:text-black transition-colors">Privacy Policy</a>
                  <a href="#" className="text-[10px] uppercase tracking-widest text-stone-400 hover:text-black transition-colors">Terms of Service</a>
               </div>
            </div>
         </div>
      </footer>

      {selectedService && (
        <ServiceDetail 
          service={selectedService} 
          onClose={() => setSelectedService(null)} 
          lang={lang} 
        />
      )}
      
      {showLocations && (
        <LocationDetail 
          onClose={() => setShowLocations(false)}
          lang={lang}
        />
      )}

      {showQuote && (
         <QuoteRequest 
            onClose={() => setShowQuote(false)}
            lang={lang}
         />
      )}

      {showTrackingSearch && (
         <TrackingSearch 
            onClose={() => setShowTrackingSearch(false)}
            onSearch={handleTrack}
            lang={lang}
         />
      )}

      {/* Reuse existing TrackingResult component for displaying tracking info */}
      {trackingData && (
         <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-4xl relative">
               <TrackingResult 
                  data={trackingData} 
                  onClose={() => setTrackingData(null)} 
               />
            </div>
         </div>
      )}

      {/* <Assistant /> AI Assistant disabled for Cloudflare deployment */}
      
      {showThankYou && <ThankYou lang={lang} />}
    </div>
  );
}

export default App;
